<app-toast-container />
<mat-sidenav-container class="h-screen bg-slate-50/50" [autosize]="true">
  <mat-sidenav #sidenav [mode]="(isHandset$ | async) ? 'over' : 'side'" [opened]="!(isHandset$ | async)" [disableClose]="!(isHandset$ | async)" [autoFocus]="false"
    class="!bg-white text-gray-800 !border-r !border-gray-200/80 shadow-xs w-72 md:w-[280px]">
    <div class="h-full w-full flex flex-col justify-between bg-slate-50/30">

      <!-- TOP: Brand Header & Customer/Venue Card -->
      <div class="flex-shrink-0 pt-4 px-3">
        <!-- Brand Header -->
        <div class="flex items-center justify-between pb-3 px-1 border-b border-gray-100">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center p-1 shadow-2xs">
              <img src="assets/images/logo.webp" alt="BistroNex" class="w-full h-full object-contain rounded-lg" />
            </div>
            <div>
              <div class="flex items-center gap-1.5">
                <span class="text-base font-black text-gray-900 tracking-tight leading-none">BistroNex</span>
                <span class="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200/60 leading-none">
                  PRO
                </span>
              </div>
              <span class="text-xs font-medium text-gray-400 block mt-1">Management Suite</span>
            </div>
          </div>

          @if (isHandset$ | async) {
          <button mat-icon-button (click)="sidenav.close()" class="!w-8 !h-8 text-gray-400 hover:text-gray-700" aria-label="Close menu">
            <mat-icon class="text-lg">close</mat-icon>
          </button>
          }
        </div>

        <!-- Customer / Venue Switcher Card -->
        <div class="pt-3">
          @if (settingsService.hasMultipleCustomers()) {
          <div class="p-2 rounded-2xl bg-white border border-gray-200/80 shadow-2xs hover:border-gray-300 transition-all">
            <div class="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1 px-1 flex items-center gap-1.5">
              <mat-icon class="text-xs w-3.5 h-3.5 text-orange-500">storefront</mat-icon>
              <span>{{ "NAV.MANAGE_CUSTOMER.TITLE" | translate }}</span>
            </div>
            <mat-form-field appearance="outline" class="w-full subscript-wrapper-hidden !m-0 !p-0">
              <mat-select [value]="settingsService.selectedMembership()" (selectionChange)="changeCustomer($event.value)"
                panelClass="!rounded-2xl !shadow-xl !border !border-gray-100 !p-1.5">
                <mat-select-trigger>
                  <div class="flex items-center justify-between w-full py-0.5 min-w-0">
                    <div class="flex items-center gap-2.5 min-w-0">
                      <div class="w-7.5 h-7.5 rounded-xl bg-orange-50 text-orange-600 border border-orange-100 flex items-center justify-center shrink-0">
                        <mat-icon class="text-base">store</mat-icon>
                      </div>
                      <div class="flex flex-col text-left min-w-0">
                        <span class="font-bold text-sm text-gray-900 truncate">
                          {{ settingsService.selectedMembership()?.Venue?.Name }}
                        </span>
                        <span class="text-xs font-semibold text-emerald-600">
                          {{ getRoleName(settingsService.selectedMembership()?.Role) | translate }}
                        </span>
                      </div>
                    </div>
                  </div>
                </mat-select-trigger>
                @for (membership of settingsService.availableMemberships(); track membership.id) {
                <mat-option [value]="membership" class="!rounded-xl !my-1">
                  <div class="flex items-center justify-between gap-3 w-full py-1">
                    <div class="flex items-center gap-2 min-w-0">
                      <div class="w-6 h-6 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                        <mat-icon class="text-xs">store</mat-icon>
                      </div>
                      <span class="text-sm font-semibold text-gray-800 truncate">{{ membership.Venue.Name }}</span>
                    </div>
                    <span class="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 shrink-0">
                      {{ getRoleName(membership.Role) | translate }}
                    </span>
                  </div>
                </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
          } @else if (settingsService.selectedMembership(); as m) {
          <div class="p-2.5 rounded-2xl bg-white border border-gray-200/80 shadow-2xs flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-xl bg-orange-50 text-orange-600 border border-orange-100 flex items-center justify-center shrink-0 shadow-2xs">
              <mat-icon class="text-lg">storefront</mat-icon>
            </div>
            <div class="min-w-0 flex-1">
              <span class="font-bold text-sm text-gray-900 block truncate">
                {{ m.Venue.Name }}
              </span>
              <span class="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md border border-emerald-100 mt-0.5">
                <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                {{ getRoleName(m.Role) | translate }}
              </span>
            </div>
          </div>
          }
        </div>
      </div>

      <!-- MIDDLE: Scrollable Navigation Sections -->
      <div class="flex-grow overflow-y-auto px-3 py-2 space-y-1.5 no-scrollbar">
        @for (section of sections; track section.key) {
        <div class="rounded-2xl transition-all duration-200" [class.bg-white]="section.expanded" [class.shadow-2xs]="section.expanded" [class.border]="section.expanded"
          [class.border-gray-200]="section.expanded">

          <!-- Section Card Button -->
          <button type="button" (click)="toggleSection(section)"
            class="w-full flex items-center justify-between p-2 rounded-2xl border transition-all duration-200 cursor-pointer group text-left select-none" [ngClass]="section.expanded
              ? 'bg-transparent border-transparent'
              : 'bg-transparent border-transparent hover:bg-white hover:border-gray-200/70 hover:shadow-2xs'">

            <div class="flex items-center gap-2.5 min-w-0">
              <div class="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-105 shadow-2xs border"
                [ngClass]="[section.iconBg, section.iconColor, section.borderColor]">
                <mat-icon class="text-lg leading-none">{{ section.icon }}</mat-icon>
              </div>
              <span class="text-sm font-bold tracking-tight text-gray-800 group-hover:text-gray-900 truncate">
                {{ section.title | translate }}
              </span>
            </div>

            <div class="flex items-center gap-1.5 shrink-0 ml-2">
              @if (isSectionActive(section) && !section.expanded) {
              <span class="w-1.5 h-1.5 rounded-full shadow-2xs" [ngClass]="section.badgeColor"></span>
              }
              <mat-icon class="text-gray-400 group-hover:text-gray-600 transition-transform duration-200 text-base w-4.5 h-4.5 flex items-center justify-center"
                [class.rotate-180]="section.expanded">
                expand_more
              </mat-icon>
            </div>
          </button>

          <!-- Collapsible Sub-items Container -->
          <div class="overflow-hidden transition-all duration-300 ease-in-out" [class.max-h-0]="!section.expanded" [class.max-h-[1000px]]="section.expanded">
            <div class="pl-3.5 pr-2 ml-4 mb-2 space-y-1 border-l-2 border-gray-100">
              @if (section.type === 'simple') {
              @for (item of section.items; track item.path) {
              @if (!item.isGroup) {
              <a [routerLink]="item.path" routerLinkActive #rla="routerLinkActive" [routerLinkActiveOptions]="{ exact: false }"
                class="flex items-center justify-between px-2.5 py-1.5 rounded-xl text-sm font-semibold transition-all duration-150 cursor-pointer no-underline group" [ngClass]="rla.isActive
                  ? 'bg-orange-50 text-orange-700 border border-orange-200/70 shadow-2xs font-bold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-transparent'">
                <div class="flex items-center gap-2 min-w-0">
                  <mat-icon class="text-base w-4 h-4 transition-colors" [ngClass]="rla.isActive ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'">
                    {{ item.icon }}
                  </mat-icon>
                  <span class="truncate">{{ item.title | translate }}</span>
                </div>
                @if (rla.isActive) {
                <span class="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></span>
                }
              </a>
              } @else {
              <!-- Sub-group in simple section -->
              <div class="space-y-1">
                <button type="button" (click)="toggleSubGroup(item)"
                  class="w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl text-sm font-semibold transition-all duration-150 cursor-pointer group text-left select-none"
                  [ngClass]="item.expanded || isSubGroupActive(item.path)
                    ? 'text-orange-950 bg-orange-50/70 border border-orange-200/60 font-bold'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-transparent'">
                  <div class="flex items-center gap-2 min-w-0">
                    <mat-icon class="text-base w-4 h-4" [ngClass]="item.expanded || isSubGroupActive(item.path) ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'">
                      {{ item.icon }}
                    </mat-icon>
                    <span class="truncate">{{ item.title | translate }}</span>
                  </div>
                  <mat-icon class="text-gray-400 group-hover:text-gray-600 transition-transform duration-200 text-sm w-4 h-4" [class.rotate-180]="item.expanded">
                    expand_more
                  </mat-icon>
                </button>

                <div class="overflow-hidden transition-all duration-300 ease-in-out" [class.max-h-0]="!item.expanded" [class.max-h-[500px]]="item.expanded">
                  <div class="pl-3 ml-2.5 my-1 space-y-1 border-l-2 border-gray-100">
                    @for (child of item.children; track child.path) {
                    <a [routerLink]="child.path" routerLinkActive #rlaChild="routerLinkActive" [routerLinkActiveOptions]="{ exact: false }"
                      class="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 cursor-pointer no-underline group" [ngClass]="rlaChild.isActive
                        ? 'bg-orange-50 text-orange-700 border border-orange-200/70 shadow-2xs font-bold'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 border border-transparent'">
                      <div class="flex items-center gap-2 min-w-0">
                        <mat-icon class="text-sm w-3.5 h-3.5" [ngClass]="rlaChild.isActive ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'">
                          {{ child.icon }}
                        </mat-icon>
                        <span class="truncate">{{ child.title | translate }}</span>
                      </div>
                      @if (rlaChild.isActive) {
                      <span class="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></span>
                      }
                    </a>
                    }
                  </div>
                </div>
              </div>
              }
              }
              } @else {
              @for (group of section.groups; track group.path) {
              <div class="space-y-1">
                <button type="button" (click)="toggleSubGroup(group)"
                  class="w-full flex items-center justify-between px-2 py-1.5 rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer group text-left select-none"
                  [ngClass]="group.expanded || isSubGroupActive(group.path)
                    ? 'text-blue-900 bg-blue-50/70 border border-blue-100'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-transparent'">
                  <div class="flex items-center gap-2 min-w-0">
                    <mat-icon class="text-base w-4 h-4" [ngClass]="group.expanded || isSubGroupActive(group.path) ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'">
                      {{ group.icon }}
                    </mat-icon>
                    <span class="truncate">{{ group.title | translate }}</span>
                  </div>
                  <mat-icon class="text-gray-400 group-hover:text-gray-600 transition-transform duration-200 text-sm w-4 h-4" [class.rotate-180]="group.expanded">
                    expand_more
                  </mat-icon>
                </button>

                <div class="overflow-hidden transition-all duration-300 ease-in-out" [class.max-h-0]="!group.expanded" [class.max-h-[500px]]="group.expanded">
                  <div class="pl-3 ml-2.5 my-1 space-y-1 border-l-2 border-gray-100">
                    @for (item of group.children; track item.path) {
                    <a [routerLink]="item.path" routerLinkActive #rlaChild="routerLinkActive" [routerLinkActiveOptions]="{ exact: false }"
                      class="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 cursor-pointer no-underline group" [ngClass]="rlaChild.isActive
                        ? 'bg-orange-50 text-orange-700 border border-orange-200/70 shadow-2xs font-bold'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 border border-transparent'">
                      <div class="flex items-center gap-2 min-w-0">
                        <mat-icon class="text-sm w-3.5 h-3.5" [ngClass]="rlaChild.isActive ? 'text-orange-600' : 'text-gray-400 group-hover:text-gray-600'">
                          {{ item.icon }}
                        </mat-icon>
                        <span class="truncate">{{ item.title | translate }}</span>
                      </div>
                      @if (rlaChild.isActive) {
                      <span class="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></span>
                      }
                    </a>
                    }
                  </div>
                </div>
              </div>
              }
              }
            </div>
          </div>
        </div>
        }
      </div>

      <!-- BOTTOM: Footer with Language Switcher & User Profile/Logout -->
      <div class="flex-shrink-0 p-3 pt-2 border-t border-gray-200/70 bg-white space-y-2.5">
        <!-- Language Selector Pill Bar -->
        <div>
          <div class="p-1 bg-gray-100/80 rounded-xl border border-gray-200/60 flex items-center gap-1">
            @for (lang of languages; track lang.code) {
            <button type="button" (click)="changeLang(lang.code)"
              class="flex-1 py-1.5 px-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all duration-150 cursor-pointer select-none" [ngClass]="translateSvc.currentLang === lang.code
                ? 'bg-white text-gray-900 shadow-2xs border border-gray-200/80 font-black'
                : 'text-gray-500 hover:text-gray-900 hover:bg-white/60 border border-transparent'">
              <span class="text-sm leading-none">{{ lang.flag }}</span>
              <span class="text-xs uppercase tracking-wider">{{ lang.code }}</span>
            </button>
            }
          </div>
        </div>

        <!-- User Profile Card with Logout Action -->
        <div class="p-2 rounded-2xl bg-gray-50/70 border border-gray-200/70 shadow-2xs flex items-center justify-between gap-2">
          <div class="flex items-center gap-2.5 min-w-0">
            <div class="w-9 h-9 rounded-xl bg-orange-100/70 text-orange-600 font-black text-sm flex items-center justify-center shrink-0 border border-orange-200/60 shadow-2xs">
              {{ userInitial }}
            </div>
            <div class="flex flex-col min-w-0">
              <span class="text-sm font-bold text-gray-900 truncate leading-snug">
                {{ userDisplayName }}
              </span>
              @if (userSubTitle) {
              <span class="text-xs text-gray-500 truncate leading-none mt-0.5">
                {{ userSubTitle | translate }}
              </span>
              }
            </div>
          </div>
          <button type="button" (click)="logout()"
            class="w-8 h-8 rounded-xl flex items-center justify-center text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer shrink-0"
            [title]="'NAV.LOGOUT' | translate">
            <mat-icon class="text-lg w-4.5 h-4.5">logout</mat-icon>
          </button>
        </div>

        <!-- Subtle status footer -->
        <div class="flex items-center justify-between px-1 text-xs font-medium text-gray-400">
          <div class="flex items-center gap-1.5">
            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>BistroNex Suite</span>
          </div>
          <span>v1.1</span>
        </div>
      </div>

    </div>
  </mat-sidenav>

  <!-- CONTENT VIEW -->
  <mat-sidenav-content class="flex flex-col h-full relative bg-surface">
    @if (isHandset$ | async) {
    <div class="bg-white/95 backdrop-blur-md border-b border-gray-200/80 h-16 flex-shrink-0 z-10 flex items-center justify-between px-4 shadow-xs">
      <div class="flex items-center gap-3">
        <button mat-icon-button (click)="sidenav.toggle()" class="text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-xl transition-colors"
          aria-label="Toggle Navigation">
          <mat-icon>menu</mat-icon>
        </button>
        <img src="assets/images/logo.webp" alt="BistroNex" class="h-10 object-contain rounded-lg shadow-2xs" />
      </div>
      @if (settingsService.selectedMembership(); as m) {
      <span class="text-xs font-bold text-gray-800 max-w-[140px] truncate bg-gray-50 border border-gray-200/60 px-2.5 py-1 rounded-lg">
        {{ m.Venue.Name }}
      </span>
      }
    </div>
    }

    <div class="flex-1 overflow-hidden relative w-full h-[calc(100vh-64px)] md:h-full">
      @if (isCustomerContextReady()) {
      <div class="overflow-y-auto h-full w-full">
        <router-outlet></router-outlet>
      </div>
      } @else {
      <div class="flex justify-center items-center h-full w-full">
        <mat-spinner></mat-spinner>
      </div>
      }
    </div>
  </mat-sidenav-content>
</mat-sidenav-container>